import {BackendResponse} from '@common/http/backend-response/backend-response';
import {PaginatedBackendResponse} from '@common/http/backend-response/pagination-response';
import {apiClient, queryClient} from '@common/http/query-client';
import {Artist} from '@app/web-player/artists/artist';
import {Genre} from '@app/web-player/genres/genre';
import {useMutation, useQuery} from '@tanstack/react-query';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';

export const musicPreferenceOnboardingPath =
  '/select-your-preferred-music-genre';

export interface MusicPreferenceStatus extends BackendResponse {
  enabled: boolean;
  required: boolean;
  completed: boolean;
  genre_id?: number | null;
  genre_ids?: number[];
  selected_artist_ids: number[];
}

export interface OnboardingGenre extends Genre {
  artists_count?: number;
  tracks_count?: number;
  popularity?: number | null;
}

export interface OnboardingArtist extends Artist {
  is_followed?: boolean;
}

export function useMusicPreferenceStatus() {
  return useQuery({
    queryKey: ['onboarding', 'music-preference', 'status'],
    queryFn: () => fetchMusicPreferenceStatus(),
  });
}

export function useOnboardingGenres(query: string) {
  return useQuery({
    queryKey: ['onboarding', 'music-preference', 'genres', query],
    queryFn: () => fetchOnboardingGenres(query),
  });
}

export function useOnboardingArtists(
  genreIds: number[],
  query: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['onboarding', 'music-preference', 'artists', genreIds, query],
    queryFn: () => fetchOnboardingArtists(genreIds, query),
    enabled,
  });
}

export function useFollowOnboardingArtist() {
  return useMutation({
    mutationFn: (artistId: number) =>
      apiClient.post(`onboarding/artist/${artistId}/follow`).then(r => r.data),
    onError: r => showHttpErrorToast(r),
  });
}

export function useCompleteMusicPreference() {
  return useMutation({
    mutationFn: (payload: {genre_ids: number[]; artist_ids: number[]}) =>
      apiClient
        .post<MusicPreferenceStatus>('onboarding/music-preference/complete', payload)
        .then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['onboarding', 'music-preference'],
      });
      queryClient.invalidateQueries({queryKey: ['artists', 'library']});
    },
    onError: r => showHttpErrorToast(r),
  });
}

function fetchMusicPreferenceStatus(): Promise<MusicPreferenceStatus> {
  return apiClient
    .get<MusicPreferenceStatus>('onboarding/music-preference/status')
    .then(r => r.data);
}

function fetchOnboardingGenres(
  query: string,
): Promise<PaginatedBackendResponse<OnboardingGenre>> {
  return apiClient
    .get<PaginatedBackendResponse<OnboardingGenre>>(
      'onboarding/music-preference/genres',
      {
        params: {
          query: query || undefined,
          per_page: 50,
        },
      },
    )
    .then(r => r.data);
}

function fetchOnboardingArtists(
  genreIds: number[],
  query: string,
): Promise<PaginatedBackendResponse<OnboardingArtist>> {
  return apiClient
    .get<PaginatedBackendResponse<OnboardingArtist>>(
      'onboarding/music-preference/artists',
      {
        params: {
          genre_ids: genreIds.length ? genreIds : undefined,
          query: query || undefined,
          per_page: 30,
        },
      },
    )
    .then(r => r.data);
}
